import { useState } from "react";
import { ChevronDown, ChevronRight, BarChart3 } from "lucide-react";

const FleetEmissionOverview = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const emissionOverview = [
    {
      id: 1,
      title: "Carbon Dioxide (CO₂)",
      formula: "CO₂ (kg) = Σ(Fuel Consumption (kg) × CO₂ Emission Factor)",
      description:
        "Daily CO₂ emissions calculated per vessel using fuel-specific emission factors from FuelTable.",
      calculationDetails: [
        "HFO CO₂ = HFO Consumption (kg) × CF_C02_HFO",
        "LFO CO₂ = LFO Consumption (kg) × CF_C02_LFO",
        "MGO CO₂ = MGO Consumption (kg) × CF_C02_MGO",
        "Total Daily CO₂ = Σ(CO₂ from all fuels)",
        "Reported as MT/day (Metric Tons per day)"
      ],
      borderColor: "border-blue-200",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
    },
    {
      id: 2,
      title: "Nitrogen Oxides (NOₓ)",
      formula: "NOₓ (kg) = Σ(Fuel Consumption by Mode × NOₓ Emission Factor)",
      description:
        "NOₓ emissions calculated based on engine type, operational mode, and specific NOₓ emission factors.",
      listItems: [
        "Main Engine HSFO/LSFO: 78.46 g/kg fuel",
        "Main Engine MDO/LSMDO: 73.75 g/kg fuel",
        "Aux Engine HSFO/LSFO: 49.37 g/kg fuel",
        "Aux Engine MDO/LSMDO: 46.37 g/kg fuel",
        "Boiler HSFO/LSFO: 6.89 g/kg fuel",
        "Boiler MDO/LSMDO: 6.47 g/kg fuel",
      ],
      calculationDetails: [
        "Separate calculations for At Sea, At Anchorage, and At Port modes",
        "Main Engine, Auxiliary Engine, and Boiler calculated separately",
        "Factors vary by fuel type and engine configuration",
        "Reported as MT/day"
      ],
      borderColor: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
    },
    {
      id: 3,
      title: "Sulfur Oxides (SOₓ)",
      formula: "SOₓ (kg) = Σ(Fuel Consumption (t) × Sulfur Content (%) × 2 × 0.97753 × 10)",
      description:
        "SOₓ emissions calculated from actual fuel sulfur content using the conversion factor 2 × 0.97753.",
      calculationDetails: [
        "For each fuel type (HSFO, LSFO, MDO, LSMDO):",
        "SOₓ = Fuel Consumption (tonnes) × Sulfur Content (%) × 2 × 0.97753 × 10",
        "Sulfur content measured from vessel reports",
        "Conversion factor accounts for molecular weight ratio (S to SO₂)",
        "Multiplied by 10 to convert from % to kg per tonne",
        "Reported as MT/day"
      ],
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
    {
      id: 4,
      title: "Particulate Matter (PM)",
      formula: "PM (kg) = Σ((Fuel Consumption / SFOC) × PM10_Kwh × 1000)",
      description:
        "PM emissions calculated using Specific Fuel Oil Consumption (SFOC) and sulfur-dependent PM emission factors.",
      calculationDetails: [
        "PM10_Kwh = 1.35 + SFOC × 7 × 0.02247 × (Sulfur Content/100 - 0.0246)",
        "SFOC values from SFOC table (e.g., HFO SSD: ~200+ g/kWh)",
        "PM (kg) = (Fuel Consumption (g) / SFOC (g/kWh)) × PM10_Kwh / 1000",
        "Uses latest sulfur content from vessel reports",
        "Separate calculations for HFO/LFO and MGO fuels",
        "Reported as MT/day"
      ],
      borderColor: "border-purple-200",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
    },
    {
      id: 5,
      title: "Methane (CH₄) & Nitrous Oxide (N₂O)",
      formulas: [
        "CH₄ (kg) = Σ(Fuel Consumption (kg) × CH₄ Emission Factor)",
        "N₂O (kg) = Σ(Fuel Consumption (kg) × N₂O Emission Factor)"
      ],
      description:
        "CH₄ and N₂O emissions calculated using fuel-specific emission factors from FuelTable.",
      calculationDetails: [
        "CH₄ from HFO = HFO Consumption (kg) × CF_CH4_HFO",
        "CH₄ from LFO = LFO Consumption (kg) × CF_CH4_LFO",
        "CH₄ from MGO = MGO Consumption (kg) × CF_CH4_MGO",
        "N₂O from HFO = HFO Consumption (kg) × CF_N20_HFO",
        "N₂O from LFO = LFO Consumption (kg) × CF_N20_LFO",
        "N₂O from MGO = MGO Consumption (kg) × CF_N20_MGO",
        "Reported as MT/day"
      ],
      borderColor: "border-teal-200",
      bgColor: "bg-teal-50",
      textColor: "text-teal-700",
    },
    {
      id: 6,
      title: "CO₂ Equivalent (CO₂e)",
      formula: "CO₂e (kg) = CO₂ + (CH₄ × 28) + (N₂O × 298)",
      description:
        "Combines all greenhouse gases into a single metric representing total global warming impact over 100 years.",
      additionalInfo:
        "Global Warming Potential factors: Methane (CH₄) is 28 times more powerful than CO₂, and Nitrous Oxide (N₂O) is 298 times more powerful over 100 years.",
      calculationDetails: [
        "Uses calculated CO₂, CH₄, and N₂O values",
        "CH₄ multiplied by 28 (GWP factor)",
        "N₂O multiplied by 298 (GWP factor)",
        "Represents Tank-to-Wake emissions only",
        "Reported as MT/day"
      ],
      borderColor: "border-rose-200",
      bgColor: "bg-rose-50",
      textColor: "text-rose-700",
    },
    {
      id: 7,
      title: "Greenhouse Gas Intensity (GHG)",
      formula: "GHG Intensity (g CO₂e/MJ) = Reward Factor × (WtT + TtW)",
      description:
        "Well-to-Wake GHG intensity measured in grams of CO₂ equivalent per megajoule of energy consumed.",
      calculationDetails: [
        "WtT (Well-to-Tank): Emissions from fuel production and transport",
        "TtW (Tank-to-Wake): Emissions from fuel combustion (including CO₂e)",
        "Reward Factor: Currently set to 1.0",
        "Accounts for carbon slip and shore power energy",
        "Based on fuel LCV (Lower Calorific Value) and RFNBO factors",
        "Compliant with IMO DCS and EU MRV methodologies"
      ],
      borderColor: "border-indigo-200",
      bgColor: "bg-indigo-50",
      textColor: "text-indigo-700",
    },
    {
      id: 8,
      title: "Daily Emission Metrics",
      formulas: [
        "Daily Average = Total Emissions / Number of Days",
        "Daily Min/Max = Statistical range across vessels",
        "All values normalized to MT/day (except GHG: g CO₂e/MJ)"
      ],
      borderColor: "border-gray-300",
      bgColor: "bg-gray-100",
      textColor: "text-gray-700",
    },
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#c5fcdd] rounded-lg">
              <BarChart3 className="w-6 h-6 text-[#27ae60]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Fleet Emission Overview
              </h2>
              <p className="text-gray-600 text-sm">
                Daily emission calculations and metrics methodology
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {emissionOverview.map((item) => (
                <div
                  key={item.id}
                  className={`p-5 rounded-lg border ${item.borderColor} ${item.bgColor}`}
                >
                  <h4 className="font-semibold text-gray-800 mb-3">
                    {item.title}
                  </h4>
                  <div className="p-3 bg-white rounded border border-gray-200 mb-3">
                    {item.formulas ? (
                      item.formulas.map((formula, index) => (
                        <p
                          key={index}
                          className={`font-semibold ${item.textColor} ${index > 0 ? 'mt-2' : ''}`}
                        >
                          {formula}
                        </p>
                      ))
                    ) : (
                      <p className={`font-semibold ${item.textColor}`}>
                        {item.formula}
                      </p>
                    )}
                  </div>
                  <p className="text-gray-600 text-sm mb-2">
                    {item.description}
                  </p>
                  
                  {item.calculationDetails && (
                    <div className="mt-3 p-2 bg-white rounded border border-gray-200">
                      <p className="text-xs font-semibold text-gray-700 mb-1">Calculation Details:</p>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {item.calculationDetails.map((detail, index) => (
                          <li key={index}>• {detail}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {item.listItems && (
                    <div className="mt-3 p-2 bg-white rounded border border-gray-200">
                      <p className="text-xs font-semibold text-gray-700 mb-1">Specific Factors:</p>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {item.listItems.map((listItem, index) => (
                          <li key={index}>• {listItem}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {item.additionalInfo && (
                    <div className="mt-3 p-2 bg-white rounded border border-gray-200">
                      <p className="text-xs font-semibold text-gray-700 mb-1">Additional Information:</p>
                      <p className="text-xs text-gray-600 italic">
                        {item.additionalInfo}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FleetEmissionOverview;